/**
 * Cockpit Shell — shared visual primitives for the Jobs-era restraint UI.
 * ─────────────────────────────────────────────────────────────────────────
 * One file, locked vocabulary. Every Cockpit-skinned page composes from
 * these primitives so density, type scale, and rhythm stay identical
 * across the platform.
 *
 *   ── Layout
 *   CockpitPage          → max-width, padding, vertical rhythm
 *   CockpitSplitLayout   → list (left) + detail (right) two-pane
 *   CockpitSection       → titled section with optional eyebrow + actions
 *
 *   ── Header
 *   CockpitMasthead      → eyebrow + title + right-anchored hero strip
 *   HeroMetric           → single-emphasis stat (hero variant + secondary)
 *   HeroDivider          → vertical hairline between hero metrics
 *   CockpitKpiStrip      → standalone hairline strip of metrics (no title)
 *
 *   ── Filters / Tabs
 *   CockpitTabs          → underlined hairline tabs with counts
 *   CockpitFilterBar     → search + chips + right-side controls, single row
 *   CockpitChipGroup     → inline filter chips (active/inactive)
 *   CockpitSearch        → ghost input with leading icon
 *
 *   ── Lists / Tables
 *   CockpitListShell     → border-y + divide-y wrapper for hairline rows
 *   CockpitColumns       → header rule for a row grid
 *   CockpitRow           → single hairline row (link or static)
 *   CockpitFinancialTable→ tabular-nums table with right-aligned numerics
 *
 *   ── Specialized layouts
 *   CockpitKanban        → horizontal scrolling column board
 *   CockpitKanbanColumn  → single column with header + stacked cards
 *   CockpitKanbanCard    → hairline card for a kanban column
 *   CockpitCalendarFrame → matte frame for embedding existing calendars
 *
 *   ── Atoms / helpers
 *   CockpitEmpty         → centered empty-state message
 *   SectionEyebrow       → small uppercase section label
 *   StatusDot            → 6px tonal dot
 *   InlineMeta           → muted dot-separated metadata line
 *   toneFor              → numeric → "good"|"warn"|"bad" helper
 *
 * Rules:
 *   • No business logic. No data fetching.
 *   • No icons by default — symbols are earned, not decorative.
 *   • All colors via semantic tokens (foreground/muted/border/success/…).
 *   • Numerics use `tabular-nums` and right-align in tables.
 */
import { ReactNode } from "react";
import { Link } from "react-router-dom";
import { Search, ChevronRight } from "lucide-react";
import { Input } from "@/components/ui/input";
import { cn } from "@/lib/utils";

/* ══ Layout ═══════════════════════════════════════════════════════ */

export function CockpitPage({ children, className }: { children: ReactNode; className?: string }) {
  return (
    <div className={cn("max-w-[1320px] mx-auto px-6 py-6 space-y-6", className)}>
      {children}
    </div>
  );
}

/**
 * Two-pane: a hairline-bordered list on the left and a detail panel on the
 * right. Both panes scroll independently. Use for queues + detail views
 * (Lead Integrity review queue, Pipeline detail, Service Tickets, etc.).
 */
export function CockpitSplitLayout({
  list, detail, listWidth = "minmax(320px, 420px)", className,
}: {
  list: ReactNode;
  detail: ReactNode;
  /** CSS grid track for the list pane */
  listWidth?: string;
  className?: string;
}) {
  return (
    <div
      className={cn("grid gap-6", className)}
      style={{ gridTemplateColumns: `${listWidth} 1fr` }}
    >
      <div className="min-w-0 border border-border/60 rounded-sm bg-background">{list}</div>
      <div className="min-w-0">{detail}</div>
    </div>
  );
}

/**
 * Titled section. Use sparingly — most pages should rely on the masthead
 * + a single list. Reserve for pages with multiple distinct datasets.
 */
export function CockpitSection({
  eyebrow, title, actions, children, className,
}: {
  eyebrow?: string;
  title?: string;
  actions?: ReactNode;
  children: ReactNode;
  className?: string;
}) {
  return (
    <section className={cn("space-y-3", className)}>
      {(eyebrow || title || actions) && (
        <div className="flex items-end justify-between gap-4">
          <div className="space-y-1 min-w-0">
            {eyebrow && <SectionEyebrow>{eyebrow}</SectionEyebrow>}
            {title && <h2 className="text-base font-medium tracking-tight text-foreground">{title}</h2>}
          </div>
          {actions && <div className="flex items-center gap-2">{actions}</div>}
        </div>
      )}
      {children}
    </section>
  );
}

/* ══ Header ═══════════════════════════════════════════════════════ */

export function CockpitMasthead({
  eyebrow, title, right, back,
}: { eyebrow?: string; title: ReactNode; right?: ReactNode; back?: ReactNode }) {
  return (
    <header className="flex flex-wrap items-end justify-between gap-6 pb-5 border-b border-border/60">
      <div className="space-y-1 min-w-0">
        {back && <div className="mb-1">{back}</div>}
        {eyebrow && <p className="text-[10px] uppercase tracking-[0.18em] text-muted-foreground font-medium">{eyebrow}</p>}
        <h1 className="text-2xl font-semibold tracking-tight text-foreground truncate">{title}</h1>
      </div>
      {right && <div className="flex items-end gap-7">{right}</div>}
    </header>
  );
}

export function HeroMetric({
  label, value, hero = false, tone = "default",
}: {
  label: string;
  value: string | number;
  hero?: boolean;
  tone?: "default" | "good" | "warn" | "bad";
}) {
  const toneCls =
    tone === "good" ? "text-success" :
    tone === "warn" ? "text-warning" :
    tone === "bad" ? "text-destructive" : "";
  return (
    <div className="text-right">
      <p className="text-[9px] uppercase tracking-[0.16em] text-muted-foreground font-medium mb-1">{label}</p>
      <p className={cn(
        "tabular-nums tracking-tight font-semibold leading-none",
        hero ? "text-3xl text-foreground" : "text-xl text-foreground/90 font-medium",
        toneCls,
      )}>{value}</p>
    </div>
  );
}

export function HeroDivider() {
  return <span className="h-9 w-px bg-border/60 self-center" />;
}

/**
 * Standalone metric strip for pages without a single dominant hero (e.g.
 * dashboards). Renders 3-6 metrics in a hairline-bordered row.
 */
export function CockpitKpiStrip({
  items,
}: {
  items: Array<{
    label: string;
    value: string | number;
    tone?: "default" | "good" | "warn" | "bad";
    hint?: string;
  }>;
}) {
  return (
    <div className="grid border-y border-border/60 divide-x divide-border/60"
         style={{ gridTemplateColumns: `repeat(${items.length}, minmax(0, 1fr))` }}>
      {items.map((it, i) => {
        const toneCls =
          it.tone === "good" ? "text-success" :
          it.tone === "warn" ? "text-warning" :
          it.tone === "bad" ? "text-destructive" : "text-foreground";
        return (
          <div key={i} className="px-5 py-4">
            <p className="text-[10px] uppercase tracking-[0.16em] text-muted-foreground font-medium mb-1.5">
              {it.label}
            </p>
            <p className={cn("text-2xl font-semibold tabular-nums tracking-tight leading-none", toneCls)}>
              {it.value}
            </p>
            {it.hint && (
              <p className="text-[11px] text-muted-foreground mt-1.5 tabular-nums">{it.hint}</p>
            )}
          </div>
        );
      })}
    </div>
  );
}

/* ══ Filters / Tabs ══════════════════════════════════════════════ */

export interface CockpitTab { value: string; label: string; count?: number | string; }

export function CockpitTabs({
  tabs, value, onChange,
}: { tabs: CockpitTab[]; value: string; onChange: (v: string) => void }) {
  return (
    <div className="flex flex-wrap items-center gap-x-6 gap-y-2 border-b border-border/60">
      {tabs.map(t => {
        const active = value === t.value;
        return (
          <button
            key={t.value}
            onClick={() => onChange(t.value)}
            className={cn(
              "relative pb-3 text-sm transition-colors tabular-nums",
              active ? "text-foreground font-medium" : "text-muted-foreground hover:text-foreground"
            )}
          >
            {t.label}
            {t.count !== undefined && <span className="text-muted-foreground/70 ml-1">{t.count}</span>}
            {active && <span className="absolute -bottom-px left-0 right-0 h-px bg-foreground" />}
          </button>
        );
      })}
    </div>
  );
}

/**
 * Single-row filter bar: search on the left, chips in the middle, custom
 * right-side controls. Use instead of stacking inputs in a card.
 */
export function CockpitFilterBar({
  search, chips, right, className,
}: {
  search?: ReactNode;
  chips?: ReactNode;
  right?: ReactNode;
  className?: string;
}) {
  return (
    <div className={cn("flex flex-wrap items-center gap-3", className)}>
      {search}
      {chips && <div className="flex flex-wrap items-center gap-1.5">{chips}</div>}
      {right && <div className="ml-auto flex items-center gap-2">{right}</div>}
    </div>
  );
}

export function CockpitChipGroup({
  options, value, onChange,
}: {
  options: Array<{ value: string; label: string; count?: number | string }>;
  value: string;
  onChange: (v: string) => void;
}) {
  return (
    <>
      {options.map(o => {
        const active = o.value === value;
        return (
          <button
            key={o.value}
            onClick={() => onChange(o.value)}
            className={cn(
              "h-7 px-2.5 rounded-sm text-xs tabular-nums transition-colors border",
              active
                ? "bg-foreground text-background border-foreground"
                : "bg-transparent text-muted-foreground border-border/60 hover:text-foreground hover:border-foreground/40"
            )}
          >
            {o.label}
            {o.count !== undefined && (
              <span className={cn("ml-1.5", active ? "text-background/70" : "text-muted-foreground/70")}>
                {o.count}
              </span>
            )}
          </button>
        );
      })}
    </>
  );
}

export function CockpitSearch({
  value, onChange, placeholder = "Search", className,
}: { value: string; onChange: (v: string) => void; placeholder?: string; className?: string }) {
  return (
    <div className={cn("relative flex-1 min-w-[220px] max-w-md", className)}>
      <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-muted-foreground" />
      <Input
        value={value}
        onChange={e => onChange(e.target.value)}
        placeholder={placeholder}
        className="pl-9 h-9 text-sm bg-transparent border-border/60 focus-visible:ring-0 focus-visible:border-foreground/40"
      />
    </div>  
  );
}

/* ══ Lists / Tables ═════════════════════════════════════════════ */

export function CockpitListShell({ children }: { children: ReactNode }) {
  return <div className="divide-y divide-border/60 border-y border-border/60">{children}</div>;
}

export function CockpitColumns({
  template, children,
}: { template: string; children: ReactNode }) {
  return (
    <div
      className="hidden md:grid gap-6 py-3 text-[10px] uppercase tracking-[0.16em] text-muted-foreground font-medium"
      style={{ gridTemplateColumns: template }}
    >
      {children}
    </div>
  );
}

export function CockpitRow({
  template, to, onClick, children, affordance = true,
}: {
  template: string;
  to?: string;
  onClick?: () => void;
  children: ReactNode;
  affordance?: boolean;
}) {
  const inner = (
    <div
      className="md:grid md:gap-6 md:items-center space-y-1 md:space-y-0"
      style={{ gridTemplateColumns: template }}
    >
      {children}
      {affordance && (
        <ChevronRight className="hidden md:block h-4 w-4 text-muted-foreground/40 group-hover:text-foreground transition-colors justify-self-end" />
      )}
    </div>
  );
  const cls = "group block px-1 py-3 transition-colors hover:bg-muted/30 focus:outline-none focus:bg-muted/30 text-left w-full";
  if (to) return <Link to={to} className={cls}>{inner}</Link>;
  if (onClick) {
    return (
      <div
        role="button"
        tabIndex={0}
        onClick={onClick}
        onKeyDown={(e) => {
          if (e.key === "Enter" || e.key === " ") {
            e.preventDefault();
            onClick();
          }
        }}
        className={cls}
      >
        {inner}
      </div>
    );
  }
  return <div className={cls}>{inner}</div>;
}

/**
 * Dense financial/numeric table. Right-aligns numeric columns and applies
 * `tabular-nums` everywhere. Use for accounting, commissions, revenue,
 * margin breakdowns. Caller controls the schema via column metadata.
 */
export interface CockpitColumn<Row> {
  key: string;
  label: string;
  /** Cell renderer */
  render: (row: Row) => ReactNode;
  /** Right-align (numeric) — default false */
  numeric?: boolean;
  /** Header colspan/visibility hint — default visible */
  className?: string;
}

export function CockpitFinancialTable<Row>({
  columns, rows, getKey, footer, empty = "No records.",
  onRowClick,
}: {
  columns: CockpitColumn<Row>[];
  rows: Row[];
  getKey: (row: Row, index: number) => string;
  footer?: ReactNode;
  empty?: string;
  onRowClick?: (row: Row) => void;
}) {
  if (!rows.length) return <CockpitEmpty message={empty} />;
  return (
    <div className="border-y border-border/60 overflow-x-auto">
      <table className="w-full text-sm tabular-nums">
        <thead>
          <tr className="text-[10px] uppercase tracking-[0.16em] text-muted-foreground font-medium">
            {columns.map(c => (
              <th
                key={c.key}
                className={cn(
                  "py-3 px-4 font-medium border-b border-border/60",
                  c.numeric ? "text-right" : "text-left",
                  c.className,
                )}
              >
                {c.label}
              </th>
            ))}
          </tr>
        </thead>
        <tbody className="divide-y divide-border/60">
          {rows.map((row, i) => (
            <tr
              key={getKey(row, i)}
              onClick={onRowClick ? () => onRowClick(row) : undefined}
              className={cn(
                "transition-colors",
                onRowClick && "cursor-pointer hover:bg-muted/30",
              )}
            >
              {columns.map(c => (
                <td
                  key={c.key}
                  className={cn(
                    "py-3 px-4 text-foreground/90",
                    c.numeric ? "text-right" : "text-left",
                    c.className,
                  )}
                >
                  {c.render(row)}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
        {footer && (
          <tfoot>
            <tr className="border-t border-border/60 bg-muted/20 text-foreground font-medium">
              {footer}
            </tr>
          </tfoot>
        )}
      </table>
    </div>
  );
}

/* ══ Specialized layouts ═══════════════════════════════════════ */

/**
 * Horizontal-scrolling column board. Each column is a `CockpitKanbanColumn`
 * containing `CockpitKanbanCard` children. Cards are hairline-bordered, no
 * shadows, no rounded — matches table density.
 */
export function CockpitKanban({ children, className }: { children: ReactNode; className?: string }) {
  return (
    <div className={cn("border-y border-border/60 overflow-x-auto", className)}>
      <div className="flex gap-px bg-border/60 min-h-[420px]">
        {children}
      </div>
    </div>
  );
}

export function CockpitKanbanColumn({
  title, count, tone = "default", children, width = 280,
}: {
  title: string;
  count?: number | string;
  tone?: "default" | "good" | "warn" | "bad";
  children: ReactNode;
  width?: number;
}) {
  const toneCls =
    tone === "good" ? "text-success" :
    tone === "warn" ? "text-warning" :
    tone === "bad" ? "text-destructive" : "text-foreground";
  return (
    <div className="flex-shrink-0 bg-background flex flex-col" style={{ width }}>
      <div className="px-4 py-3 border-b border-border/60 flex items-center justify-between">
        <p className="text-[10px] uppercase tracking-[0.16em] font-medium text-muted-foreground">{title}</p>
        {count !== undefined && (
          <span className={cn("text-xs tabular-nums font-medium", toneCls)}>{count}</span>
        )}
      </div>
      <div className="flex-1 overflow-y-auto divide-y divide-border/60">
        {children}
      </div>
    </div>
  );
}

export function CockpitKanbanCard({
  to, onClick, children,
}: { to?: string; onClick?: () => void; children: ReactNode }) {
  const cls = "block px-4 py-3 hover:bg-muted/30 transition-colors text-left w-full";
  if (to) return <Link to={to} className={cls}>{children}</Link>;
  if (onClick) return <button type="button" onClick={onClick} className={cls}>{children}</button>;
  return <div className={cls}>{children}</div>;
}

/**
 * Frame for embedding an existing calendar component (e.g. the role-aware
 * scheduling calendar) without restyling its internals. Provides the
 * Cockpit-correct outer chrome (hairline border, matte background).
 */
export function CockpitCalendarFrame({ children }: { children: ReactNode }) {
  return (
    <div className="border border-border/60 rounded-sm bg-background overflow-hidden">
      {children}
    </div>
  );
}

/* ══ Atoms / helpers ═════════════════════════════════════════════ */

export function CockpitEmpty({ message }: { message: string }) {
  return (
    <div className="py-20 text-center">
      <p className="text-base text-muted-foreground">{message}</p>
    </div>
  );
}

export function SectionEyebrow({ children }: { children: ReactNode }) {
  return (
    <p className="text-[10px] uppercase tracking-[0.18em] text-muted-foreground font-medium">
      {children}
    </p>
  );
}

/** 6px tonal dot — for status indicators inside dense rows. */
export function StatusDot({ tone = "default" }: { tone?: "default" | "good" | "warn" | "bad" | "muted" }) {
  const cls =
    tone === "good" ? "bg-success" :
    tone === "warn" ? "bg-warning" :
    tone === "bad" ? "bg-destructive" :
    tone === "muted" ? "bg-muted-foreground/40" :
    "bg-foreground/60";
  return <span className={cn("inline-block h-1.5 w-1.5 rounded-full", cls)} />;
}

/**
 * Dot-separated muted metadata line (e.g. "Tampa · 3 jobs · Updated 2h ago").
 * Filters falsy children automatically.
 */
export function InlineMeta({
  items, className,
}: { items: Array<ReactNode | false | null | undefined>; className?: string }) {
  const visible = items.filter(Boolean) as ReactNode[];
  return (
    <p className={cn("text-xs text-muted-foreground tabular-nums", className)}>
      {visible.map((node, i) => (
        <span key={i}>
          {i > 0 && <span className="mx-1.5 text-muted-foreground/40">·</span>}
          {node}
        </span>
      ))}
    </p>
  );
}

/* ── Tone helpers ────────────────────────────────────────────────── */

export function toneFor(value: number, good: number, warn: number): "good" | "warn" | "bad" {
  if (value >= good) return "good";
  if (value >= warn) return "warn";
  return "bad";
}
